import { Tag, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"

import Flex from "#blocks/Flex"
import { camelToWords } from "#utils/textFormatters"

import { ESSAY_LABELS } from "./portfolioEssayConstants"
import { usePortfolioAuthorName } from "./usePortfolioAuthorDisplay"

import type {
  PortfolioMockProject,
  PortfolioMockStage,
  PortfolioTimelineItem,
} from "./portfolioUiTypes"

dayjs.extend(relativeTime)

const getTimelineTypeLabel = (item: PortfolioTimelineItem): string => {
  if (item.type === "comment") return "Comment"
  // Older rows may still carry the retired `planningPriorityRank` field key;
  // their messages say "priority", so the message check covers them.
  const isPriorityEvent =
    item.eventFieldKey === "planningGroup" ||
    item.message.toLowerCase().includes("priority")
  if (isPriorityEvent) return "Priority"

  const normalizedMessage = item.message.trim().toLowerCase()
  if (
    normalizedMessage.startsWith("created ") ||
    normalizedMessage.startsWith("added ")
  ) {
    return "New"
  }

  return "Edit"
}

const FIELD_LABELS: Record<string, string> = {
  title: "Title",
  notes: "Notes",
  coverImageUrl: "Cover Image",
  category: "Category",
  planningGroup: "Priority Group",
  tagIds: "Tags",
  dimensions: "Dimensions",
  materials: "Materials",
  processes: "Processes",
  ideas: "Ideas",
  label: "Step Name",
  dueDate: "Due Date",
  medium: "Medium",
  teacherComment: "Teacher Comment",
  feedback: "Feedback",
  rejectionReason: "Feedback Reason",
  status: "Status",
  ...ESSAY_LABELS,
}

const CommentContent = ({
  item,
  statusColorMap,
  stageLabel,
}: {
  item: PortfolioTimelineItem
  statusColorMap: Record<PortfolioMockStage["status"], string>
  stageLabel?: string
}) => {
  const { Text } = Typography

  if (item.type !== "event" || !item.eventFieldKey || !item.eventScope) {
    return (
      <Text type="secondary" style={{ fontSize: 11 }}>
        {item.message}
      </Text>
    )
  }

  const fieldLabel =
    FIELD_LABELS[item.eventFieldKey] ?? camelToWords(item.eventFieldKey)
  const baseLabel = fieldLabel
  const fullLabel =
    item.eventScope === "item" && stageLabel
      ? `${baseLabel} on ${stageLabel}`
      : baseLabel
  const nextValue = item.eventNextValue ?? ""

  if (item.eventFieldKey === "imageUrl") {
    return (
      <Text type="secondary" style={{ fontSize: 11 }}>
        {item.message}
      </Text>
    )
  }

  if (!nextValue.trim()) {
    return (
      <Text type="secondary" style={{ fontSize: 11 }}>
        Cleared {fullLabel}.
      </Text>
    )
  }

  if (item.eventFieldKey === "status") {
    const normalizedStatus = nextValue
      .toLowerCase()
      .replace(/\s+/g, "_") as PortfolioMockStage["status"]
    const badgeColor = statusColorMap[normalizedStatus] ?? "default"
    return (
      <Text type="secondary" style={{ fontSize: 11 }}>
        {fullLabel} →{" "}
        <Tag
          color={badgeColor}
          style={{ fontSize: 11, lineHeight: "20px", margin: 0 }}
        >
          {nextValue}
        </Tag>
      </Text>
    )
  }

  const highlightedValueFields = new Set([
    "title",
    "label",
    "notes",
    "teacherComment",
    "feedback",
    "medium",
    "dimensions",
    "materials",
    "processes",
    "ideas",
    "planningGroup",
    "sustainedInquiryQuestion",
    "sustainedPracticeEvidence",
    "tagIds",
  ])
  if (highlightedValueFields.has(item.eventFieldKey)) {
    return (
      <Text type="secondary" style={{ fontSize: 11 }}>
        {fullLabel} →{" "}
        <span
          style={{
            backgroundColor: "#f3f3f3",
            padding: "2px 5px",
            borderRadius: 3,
            color: "rgba(0, 0, 0, 0.88)",
            fontSize: 11,
          }}
        >
          {nextValue}
        </span>
      </Text>
    )
  }

  return (
    <Text type="secondary" style={{ fontSize: 11 }}>
      {fullLabel} → {nextValue}
    </Text>
  )
}

const TimelineAuthorName = ({
  authorId,
  authorKind,
  secondary,
}: {
  authorId: string
  authorKind?: PortfolioTimelineItem["authorKind"]
  secondary?: boolean
}) => {
  const { Text } = Typography
  const name = usePortfolioAuthorName(authorId, authorKind)
  return (
    <Text
      strong={!secondary}
      type={secondary ? "secondary" : undefined}
      style={{ fontSize: secondary ? 10 : 12 }}
    >
      {name || authorId}
    </Text>
  )
}

const PortfolioComment = ({
  item,
  projectState,
  statusColorMap,
  onClick,
  currentUserId,
}: {
  item: PortfolioTimelineItem
  projectState: PortfolioMockProject[]
  statusColorMap: Record<PortfolioMockStage["status"], string>
  onClick: (item: PortfolioTimelineItem) => void
  currentUserId?: string
}) => {
  const { Text } = Typography
  const stageLabel =
    item.eventScope === "item" && item.eventEntityId
      ? projectState
          .flatMap((project) => project.stages)
          .find((stage) => stage.id === item.eventEntityId)?.label
      : undefined
  const typeLabel = getTimelineTypeLabel(item)
  const isComment = typeLabel === "Comment"
  const isOwn = !!currentUserId && item.createdBy === currentUserId
  const relativeTimeLabel = dayjs(item.createdAt).fromNow()
  const exactTimeLabel = dayjs(item.createdAt).format("MMM D, YYYY h:mm A")

  if (isComment) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: "8px 12px",
          borderRadius: 8,
          background: isOwn ? "#e6f4ff" : "#f5f5f5",
          alignSelf: isOwn ? "flex-end" : "flex-start",
          maxWidth: "85%",
        }}
      >
        <Flex style={{ gap: 6, alignItems: "baseline" }}>
          <TimelineAuthorName
            authorId={item.createdBy}
            authorKind={item.authorKind}
          />
          <Tooltip title={exactTimeLabel}>
            <Text type="secondary" style={{ fontSize: 10 }}>
              {relativeTimeLabel}
            </Text>
          </Tooltip>
        </Flex>
        <Text style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>
          {item.message}
        </Text>
      </div>
    )
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        padding: "4px 0",
        cursor: item.eventScope === "item" ? "pointer" : undefined,
      }}
      onClick={() => onClick(item)}
    >
      <Flex gap={6} style={{ alignItems: "center", maxWidth: 480 }}>
        <CommentContent
          item={item}
          statusColorMap={statusColorMap}
          stageLabel={stageLabel}
        />
        <Text type="secondary" style={{ fontSize: 10, flexShrink: 0 }}>
          <TimelineAuthorName
            authorId={item.createdBy}
            authorKind={item.authorKind}
            secondary
          />{" "}
          ·{" "}
          <Tooltip title={exactTimeLabel}>
            <span>{relativeTimeLabel}</span>
          </Tooltip>
        </Text>
      </Flex>
    </div>
  )
}

export default PortfolioComment
